// React and related imports
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Flowbite components
import {
  Alert,
  Button,
  Card,
  Label,
  TextInput,
  Spinner,
  Radio,
} from 'flowbite-react';

// React Icons
import { HiInformationCircle } from 'react-icons/hi';
import { HiOutlineCloudArrowUp } from 'react-icons/hi2';

// i18next for translations
import { useTranslation } from 'react-i18next';

// Redux API hooks
import { useCreateBroadcastMutation } from '../redux/api/broadcastApi';
import { useFetchMediaQuery } from '../redux/api/mediaApi';

// Redux selectors and hooks
import { useAppSelector } from '../redux/hooks/useAppSelector';
import {
  selectIsDarkMode,
  selectIsRTL,
} from '../redux/selectors/displaySettingsSelectors';

// Interfaces
import { IMedia } from '../common/interfaces/Media.interface';

// Interfaces
import { IMultiLanguageValue } from '../common/interfaces/MultiLanguageValue.interface';

// Enums
import { BroadcastType } from '../common/enums/BroadcastType';

// Custom components
import { PageHeader } from '../components/Shared/PageHeader';
import { MediaGalleryModal } from '../components/MediaGallery/MediaGalleryModal';
import { DatePickerInput } from '../components/Shared/DataPickerInput/DatePickerInput';
import { AlertContentVisibility } from '../components/Shared/AlertContentVisibility';
import { MultiLanguageFieldGroup } from '../components/Shared/MultiLanguageInput';
import { FilePreview } from '../components/Shared/FilePreview';

// Custom hooks
import { usePageTitle } from '../hooks/usePageTitle';

// Broadcast utils
import {
  validateBroadcastForm,
  buildBroadcastFormData,
} from '../components/Broadcast/utils';

// Date picker imports
import { registerLocale } from 'react-datepicker';
import { ar } from 'date-fns/locale/ar';

// Register Arabic locale
registerLocale('ar', ar);

export const BroadcastCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const isRtl = useAppSelector(selectIsRTL);
  const locale = isRtl ? 'ar' : '';

  usePageTitle(t('pageTitles.createBroadcast'));

  const [createBroadcast, { isLoading }] = useCreateBroadcastMutation();
  const { data: mediaData } = useFetchMediaQuery({ page: 1, size: 50 });

  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<IMedia | null>(null);

  // Multi-language state
  const [multiLangTitle, setMultiLangTitle] = useState<IMultiLanguageValue[]>(
    []
  );
  const [multiLangBody, setMultiLangBody] = useState<IMultiLanguageValue[]>([]);
  const [multiLangMessageTitle, setMultiLangMessageTitle] = useState<
    IMultiLanguageValue[]
  >([]);
  const [multiLangMessageBody, setMultiLangMessageBody] = useState<
    IMultiLanguageValue[]
  >([]);

  // Broadcast type state
  const [broadcastType, setBroadcastType] = useState<BroadcastType>(
    BroadcastType.Info
  );

  // ADS metadata state
  const [multiLangCtaText, setMultiLangCtaText] = useState<
    IMultiLanguageValue[]
  >([]);
  const [externalUrl, setExternalUrl] = useState<string>('');

  const handleSelectMedia = (media: IMedia | null) => {
    setSelectedMedia(media);
  };

  const handleCloseMediaModal = () => {
    setShowMediaGallery(false);
  };

  const handleSubmitSelectedMedia = () => {
    setShowMediaGallery(false);
  };

  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [deepLink, setDeepLink] = useState<string>('');
  const [screenName, setScreenName] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);

  const buttonClass = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  // Validation logic
  const validateForm = () => {
    const newErrors = validateBroadcastForm({
      title: multiLangTitle,
      body: multiLangBody,
      messageTitle: multiLangMessageTitle,
      messageBody: multiLangMessageBody,
      file,
      scheduledAt,
      type: broadcastType,
      ctaText: multiLangCtaText,
      externalUrl: externalUrl,
    });
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // Re-validate on form changes
  React.useEffect(() => {
    validateForm();
  }, [
    multiLangTitle,
    multiLangBody,
    multiLangMessageTitle,
    multiLangMessageBody,
    file,
    scheduledAt,
    broadcastType,
    multiLangCtaText,
    externalUrl,
  ]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleCancelFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReplaceFile = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !file || !scheduledAt) {
      return;
    }

    const formData = buildBroadcastFormData({
      file,
      scheduledAt,
      title: multiLangTitle,
      body: multiLangBody,
      messageTitle: multiLangMessageTitle,
      messageBody: multiLangMessageBody,
      type: broadcastType,
      mediaId: selectedMedia?.id,
      deepLink: deepLink || undefined,
      screenName: screenName || undefined,
      ctaText:
        broadcastType === BroadcastType.Ads ? multiLangCtaText : undefined,
      externalUrl:
        broadcastType === BroadcastType.Ads
          ? externalUrl || undefined
          : undefined,
    });

    try {
      await createBroadcast(formData).unwrap();
      navigate('/broadcast');
    } catch (error) {
      // Toast middleware will handle the error message
    }
  };

  return (
    <>
      <PageHeader title={t('broadcast.createBroadcast')} />

      {/* Validation Alert */}
      {errors && errors.length > 0 && (
        <Alert
          className="m-auto mb-4 w-11/12"
          withBorderAccent
          additionalContent={<AlertContentVisibility t={t} errors={errors} />}
          color="warning"
          icon={HiInformationCircle}
        >
          <span className="me-1 ms-1 font-extrabold">
            {t('alert.validationRequired')}
          </span>
          {t('alert.broadcastAddMessage')}
        </Alert>
      )}

      <Card className="mx-auto mb-6 w-11/12">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Multi-language Fields */}
          <MultiLanguageFieldGroup
            fields={[
              {
                id: 'title',
                label: 'broadcast.displayTitle',
                type: 'text',
                placeholder: 'placeholders.broadcastTitle',
                required: true,
                validation: {
                  maxLength: 100,
                },
              },
              {
                id: 'body',
                label: 'broadcast.displayBody',
                type: 'text',
                placeholder: 'placeholders.broadcastBody',
                required: true,
                validation: {
                  maxLength: 300,
                },
              },
              {
                id: 'messageTitle',
                label: 'broadcast.messageTitle',
                type: 'text',
                placeholder: 'placeholders.broadcastMessageTitle',
                required: true,
                validation: {
                  maxLength: 100,
                },
              },
              {
                id: 'messageBody',
                label: 'broadcast.messageBody',
                type: 'textarea',
                placeholder: 'placeholders.broadcastMessageBody',
                required: true,
                validation: {
                  maxLength: 300,
                },
              },
            ]}
            values={{
              title: multiLangTitle,
              body: multiLangBody,
              messageTitle: multiLangMessageTitle,
              messageBody: multiLangMessageBody,
            }}
            onChange={(fieldId, value) => {
              if (fieldId === 'title') {
                setMultiLangTitle(value);
              } else if (fieldId === 'body') {
                setMultiLangBody(value);
              } else if (fieldId === 'messageTitle') {
                setMultiLangMessageTitle(value);
              } else if (fieldId === 'messageBody') {
                setMultiLangMessageBody(value);
              }
            }}
            sharedLanguageSelector={true}
            t={t}
            languageSelectorLabel="broadcast.selectBroadcastLanguage"
          />

          {/* Excel File Upload */}
          <div>
            <Label htmlFor="file">
              {t('broadcast.usersFile')}
              <span className="ms-1 text-red-500">*</span>
            </Label>
            <div className="mt-2">
              {!file ? (
                <label
                  htmlFor="file-upload"
                  className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-10 transition-colors hover:border-primary-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-primary-500 dark:hover:bg-gray-700"
                >
                  <HiOutlineCloudArrowUp className="mb-3 h-10 w-10 text-gray-400 dark:text-gray-500" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {t('media.chooseFile')}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {t('broadcast.fileHelperText')}
                  </p>
                </label>
              ) : (
                <FilePreview
                  file={file}
                  onRemove={handleCancelFile}
                  onReplace={handleReplaceFile}
                />
              )}
              <input
                ref={fileInputRef}
                id="file-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Broadcast Type Selection */}
          <div>
            <Label value={t('broadcast.broadcastType')} />
            <div className="mt-2 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Radio
                  id="type-info"
                  name="broadcastType"
                  value={BroadcastType.Info}
                  checked={broadcastType === BroadcastType.Info}
                  onChange={() => setBroadcastType(BroadcastType.Info)}
                />
                <Label htmlFor="type-info">
                  {t('broadcast.broadcastTypeInfo')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Radio
                  id="type-ads"
                  name="broadcastType"
                  value={BroadcastType.Ads}
                  checked={broadcastType === BroadcastType.Ads}
                  onChange={() => setBroadcastType(BroadcastType.Ads)}
                />
                <Label htmlFor="type-ads">
                  {t('broadcast.broadcastTypeAds')}
                </Label>
              </div>
            </div>
          </div>

          {/* ADS Metadata - Conditional Fields */}
          {broadcastType === BroadcastType.Ads && (
            <Card className="bg-primary-25 dark:bg-indigo-600">
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                {t('broadcast.adsMetadata')}
              </h3>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                {t('broadcast.adsMetadataHelper')}
              </p>

              {/* CTA Text - Multi-language */}
              <MultiLanguageFieldGroup
                fields={[
                  {
                    id: 'ctaText',
                    label: 'broadcast.ctaText',
                    type: 'text',
                    placeholder: 'placeholders.ctaText',
                    required: true,
                    validation: {
                      maxLength: 50,
                    },
                    languageSelectorLabel: 'broadcast.selectCtaTextLanguage',
                  },
                ]}
                values={{
                  ctaText: multiLangCtaText,
                }}
                onChange={(fieldId, value) => {
                  if (fieldId === 'ctaText') {
                    setMultiLangCtaText(value);
                  }
                }}
                sharedLanguageSelector={false}
                t={t}
              />

              {/* External URL */}
              <div className="mt-4">
                <Label htmlFor="externalUrl">
                  {t('broadcast.externalUrl')}
                  <span className="ms-1 text-red-500">*</span>
                </Label>
                <TextInput
                  id="externalUrl"
                  type="url"
                  placeholder={t('broadcast.externalUrlPlaceholder')}
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  required
                  helperText={t('broadcast.externalUrlHelper')}
                />
              </div>
            </Card>
          )}

          {/* Scheduled Time using DatePickerInput */}
          <div>
            <Label htmlFor="scheduledAt">
              {t('broadcast.scheduledAt')}
              <span className="ms-1 text-red-500">*</span>
            </Label>
            <div className="mt-2">
              <DatePickerInput
                selected={scheduledAt || undefined}
                selectedDays={scheduledAt ? [scheduledAt] : []}
                onChange={(date) => setScheduledAt(date)}
                placeholder={t('broadcast.scheduledAt')}
                minDate={new Date()}
                locale={locale}
                className="w-full"
                showTimeSelect={true}
                timeFormat="HH:mm"
                timeIntervals={15}
              />
            </div>
          </div>

          {/* Media Selection */}
          <div className="flex items-center space-x-2">
            <TextInput
              type="text"
              value={selectedMedia?.name || ''}
              disabled
              placeholder={t('broadcast.selectMedia')}
              className="me-4 w-3/5"
            />
            <Button
              type="button"
              className={`${buttonClass} flex-shrink-0`}
              onClick={() => setShowMediaGallery(true)}
            >
              {t('content.chooseMedia')}
            </Button>
          </div>

          {/* Optional: Deep Link */}
          <div>
            <Label htmlFor="deepLink" value={t('broadcast.deepLink')} />
            <TextInput
              id="deepLink"
              type="text"
              placeholder="tazkiah://content/123"
              value={deepLink}
              onChange={(e) => setDeepLink(e.target.value)}
            />
          </div>

          {/* Optional: Screen Name */}
          <div>
            <Label htmlFor="screenName" value={t('broadcast.screenName')} />
            <TextInput
              id="screenName"
              type="text"
              placeholder={t('broadcast.screenNamePlaceholder')}
              value={screenName}
              onChange={(e) => setScreenName(e.target.value)}
            />
          </div>

          {/* Action Button */}
          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              disabled={isLoading || errors.length > 0}
              className={buttonClass}
            >
              {isLoading && (
                <Spinner color="purple" size="sm" className="mr-2" />
              )}
              {t('broadcast.submit')}
            </Button>
          </div>
        </form>
      </Card>

      {/* Media Gallery Modal */}
      {showMediaGallery && mediaData && (
        <MediaGalleryModal
          show={showMediaGallery}
          mediaItems={mediaData.data}
          onClose={handleCloseMediaModal}
          onSelect={handleSelectMedia}
          onSubmit={handleSubmitSelectedMedia}
          selectedMedia={selectedMedia ?? undefined}
        />
      )}
    </>
  );
};
